import { Box, CalendarClock, CalendarDays, MoreVertical, Move, Stethoscope } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { formatDate } from "@/components/prosthesis/prosthesis-meta";
import { codigoDoServico } from "@/lib/codigo-do-pedido";
import type { ServiceRow } from "../production-types";

type Props = {
  row: ServiceRow;
  onAbrir: () => void;
  onMover: () => void;
  podeMover: boolean;
};

/**
 * Um trabalho dentro do estágio expandido, no celular.
 *
 * Mesma informação do card do quadro — paciente, clínica, dentista, data do pedido, prazo e
 * serviço — só que em pilha vertical, que é o que cabe em 320px. Cada linha tem seu ícone pra ser
 * reconhecida sem depender de rótulo escrito.
 *
 * Mover entre estágios vive no menu, não no arraste: arrastar pra uma coluna que não está na tela
 * não é gesto que exista no celular.
 */
export function MobileProductionCard({ row, onAbrir, onMover, podeMover }: Props) {
  const { order, service } = row;
  const servico = service?.serviceCategory?.label;
  const dente = service?.teeth;

  return (
    <li className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-border dark:bg-card">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onAbrir}
          className="min-w-0 flex-1 text-left"
          aria-label={`Abrir trabalho de ${order.patientName}`}
        >
          <span className="block break-words text-sm font-bold leading-snug text-zinc-900 dark:text-foreground">
            <span className="mr-1.5 font-mono text-[11px] font-semibold text-zinc-400 dark:text-muted-foreground">#{codigoDoServico(order, row.service ?? {})}</span>
            {order.patientName}
          </span>
          <span className="mt-0.5 block truncate text-[13px] text-zinc-500 dark:text-muted-foreground">
            {order.organization.name}
          </span>

          <span className="mt-1.5 flex flex-col gap-1 text-[13px] text-zinc-600 dark:text-muted-foreground">
            {order.dentistName && (
              <span className="flex items-center gap-1.5">
                <Stethoscope className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                <span className="min-w-0 truncate">{order.dentistName}</span>
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
              Pedido: {formatDate(order.createdAt)}
            </span>
            {order.deadline && (
              <span className="flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                Prazo: {formatDate(order.deadline)}
              </span>
            )}
          </span>

          {servico && (
            <span className="mt-2 flex items-center gap-1.5">
              <Box className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
              <span className="inline-block max-w-full break-words rounded-full bg-brand-50 px-2.5 py-1 text-[12.5px] font-medium text-brand-700 dark:bg-primary/15 dark:text-primary">
                {servico}
                {dente ? ` (${dente})` : ""}
              </span>
            </span>
          )}
        </button>

        {podeMover && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="-mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted"
                aria-label={`Ações do trabalho de ${order.patientName}`}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[180px] border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card"
            >
              <DropdownMenuItem
                onClick={onMover}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm"
              >
                <Move className="h-4 w-4 text-zinc-500 dark:text-muted-foreground" />
                Mover para…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </li>
  );
}
